import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import useBranch from '../../hooks/useBranch';
import useOldBookingImport from '../../hooks/useOldBookingImport';

const jobStorageKeyPrefix = 'oldBookingImport.importJobId.';
const allBranchesValue = '__all__';

function StatCard({ title, value, subtitle, color = 'default' }) {
  return (
    <Card variant='outlined' sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant='overline' color='text.secondary'>
          {title}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography variant='h4' sx={{ fontWeight: 700 }}>
            {value}
          </Typography>
          {subtitle ? <Chip size='small' color={color} label={subtitle} /> : null}
        </Box>
      </CardContent>
    </Card>
  );
}

export default function OldBookingImport() {
  const { getBranches } = useBranch();
  const { isLoading, startImport, getImportJob, getLatestImportJob } = useOldBookingImport();

  const dateFromRef = useRef(null);
  const dateToRef = useRef(null);
  const pollRef = useRef(null);

  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(allBranchesValue);
  const [includeCanceled, setIncludeCanceled] = useState(true);
  const [job, setJob] = useState(null);

  useEffect(() => {
    let active = true;
    const loadBranches = async () => {
      try {
        const response = await getBranches();
        if (!active) return;
        setBranches(Array.isArray(response) ? response : []);
      } catch (err) {
        toast.error('Fehler beim Laden der Standorte');
      }
    };
    loadBranches();
    return () => {
      active = false;
    };
  }, [getBranches]);

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const buildPayload = useCallback(() => {
    const dateFrom = dateFromRef.current?.value;
    const dateTo = dateToRef.current?.value;
    if (!dateFrom || !dateTo) {
      toast.info('Bitte Zeitraum auswaehlen.');
      return null;
    }
    if (dayjs(dateFrom).isAfter(dayjs(dateTo))) {
      toast.info('Das Startdatum darf nicht nach dem Enddatum liegen.');
      return null;
    }
    return {
      ...(selectedBranchId !== allBranchesValue ? { branchId: selectedBranchId } : {}),
      dateFrom,
      dateTo,
      includeCanceled,
    };
  }, [includeCanceled, selectedBranchId]);

  const pollJob = useCallback(
    (jobId, options = {}) => {
      const { silent = false } = options;
      if (pollRef.current) clearInterval(pollRef.current);
      const tick = async () => {
        const response = await getImportJob(jobId);
        const nextJob = response?.job || null;
        setJob(nextJob);
        const status = String(nextJob?.status || '');
        if (status === 'finished' || status === 'finishedWithErrors' || status === 'failed') {
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
          if (!silent && status === 'finished') {
            toast.success('Old-Bookings-Import abgeschlossen');
          }
          if (!silent && status === 'finishedWithErrors') toast.warning('Old-Bookings-Import mit Fehlern abgeschlossen');
          if (!silent && status === 'failed') {
            toast.error('Old-Bookings-Import fehlgeschlagen');
          }
        }
      };
      tick();
      pollRef.current = setInterval(tick, 2500);
    },
    [getImportJob]
  );

  useEffect(() => {
    if (!selectedBranchId) return;
    let active = true;
    const storageKey = `${jobStorageKeyPrefix}${selectedBranchId}`;
    const latestBranchId = selectedBranchId !== allBranchesValue ? selectedBranchId : '';
    const restoreJob = async () => {
      try {
        const storedJobId = localStorage.getItem(storageKey);
        if (storedJobId) {
          const response = await getImportJob(storedJobId);
          if (!active) return;
          const restoredJob = response?.job || null;
          if (restoredJob) {
            setJob(restoredJob);
            const status = String(restoredJob.status || '');
            if (['created', 'queued', 'running'].includes(status)) {
              pollJob(storedJobId, { silent: true });
            }
            return;
          }
        }
      } catch (_) {
        localStorage.removeItem(storageKey);
      }

      try {
        const response = await getLatestImportJob(latestBranchId);
        if (!active) return;
        const latestJob = response?.job || null;
        if (!latestJob) return;
        setJob(latestJob);
        localStorage.setItem(storageKey, String(latestJob._id));
        const status = String(latestJob.status || '');
        if (['created', 'queued', 'running'].includes(status)) {
          pollJob(latestJob._id, { silent: true });
        }
      } catch (_) {}
    };

    restoreJob();
    return () => {
      active = false;
    };
  }, [getImportJob, getLatestImportJob, pollJob, selectedBranchId]);

  const handleImport = useCallback(async () => {
    const payload = buildPayload();
    if (!payload) return;
    setJob(null);
    const response = await startImport(payload);
    if (response?.jobId) {
      const storageKey = `${jobStorageKeyPrefix}${payload.branchId || allBranchesValue}`;
      localStorage.setItem(storageKey, String(response.jobId));
      toast.success('Old-Bookings-Import wurde gestartet');
      pollJob(response.jobId);
    }
  }, [buildPayload, pollJob, startImport]);

  const result = job?.resultData || null;
  const summary = result?.summary || {};
  const checkpoint = result?.checkpoint || {};
  const hasSummary = Boolean(result?.summary);
  const progress = Number(job?.progressPercentage || 0);
  const jobRunning = ['created', 'queued', 'running'].includes(String(job?.status || ''));

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant='h4' sx={{ fontWeight: 700 }}>
            Old Bookings Import
          </Typography>
          <Typography color='text.secondary'>
            Importiert historische Regiondo-Buchungen, die in der bestehenden Booking-Collection fehlen,
            in die separate oldBookings Collection.
          </Typography>
        </Box>

        <Paper variant='outlined' sx={{ p: 2 }}>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size='small'>
                <InputLabel>Standort</InputLabel>
                <Select
                  value={selectedBranchId}
                  label='Standort'
                  onChange={event => {
                    setSelectedBranchId(event.target.value);
                    setJob(null);
                  }}
                >
                  <MenuItem value={allBranchesValue}>Alle Standorte</MenuItem>
                  {branches.map(branch => (
                    <MenuItem key={branch._id} value={branch._id}>
                      {branch.longName || branch.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <input
                ref={dateFromRef}
                type='date'
                defaultValue={dayjs().subtract(5, 'year').format('YYYY-MM-DD')}
                style={{ width: '100%', height: 38, padding: '0 12px' }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <input
                ref={dateToRef}
                type='date'
                defaultValue={dayjs().subtract(1, 'day').format('YYYY-MM-DD')}
                style={{ width: '100%', height: 38, padding: '0 12px' }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeCanceled}
                    onChange={event => {
                      setIncludeCanceled(event.target.checked);
                      setJob(null);
                    }}
                  />
                }
                label='Stornos einschliessen'
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Alert severity='info' sx={{ py: 0 }}>
                {selectedBranchId === allBranchesValue
                  ? 'Alle Regiondo-Erlebnisse'
                  : 'Alle Regiondo-Erlebnisse des Standorts'}
              </Alert>
            </Grid>
            <Grid item xs={12}>
              <Stack direction='row' spacing={1}>
                <Button
                  variant='contained'
                  onClick={handleImport}
                  disabled={isLoading || jobRunning}
                >
                  Import starten
                </Button>
                {isLoading ? <CircularProgress size={24} /> : null}
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        {hasSummary ? (
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <StatCard title='Regiondo' value={summary.regiondoCount || 0} subtitle='gefunden' />
            </Grid>
            <Grid item xs={12} md={3}>
              <StatCard title='Bereits in booking' value={summary.existingInBooking || 0} color='warning' />
            </Grid>
            <Grid item xs={12} md={3}>
              <StatCard title='Bereits in oldBookings' value={summary.existingInOldBookings || 0} color='info' />
            </Grid>
            <Grid item xs={12} md={3}>
              <StatCard
                title='Importierbar'
                value={summary.importable || 0}
                subtitle={`${summary.entertainmentCount || 0} Erlebnisse`}
                color='success'
              />
            </Grid>
          </Grid>
        ) : null}

        {job ? (
          <Paper variant='outlined' sx={{ p: 2 }}>
            <Stack spacing={1}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <Typography variant='h6'>Importstatus</Typography>
                <Chip label={job.status} />
              </Box>
              <LinearProgress variant='determinate' value={Math.max(0, Math.min(100, progress))} />
              {result ? (
                <Typography variant='body2' color='text.secondary'>
                  Phase: {result.phase || '-'}
                  {result.currentEntertainment ? `, aktuell: ${result.currentEntertainment}` : ''}
                  {checkpoint.completedEntertainmentCount != null && checkpoint.totalEntertainmentCount != null
                    ? `, erledigte Erlebnisse: ${checkpoint.completedEntertainmentCount}/${checkpoint.totalEntertainmentCount}`
                    : ''}
                  {checkpoint.currentBranchId ? `, Standort: ${checkpoint.currentBranchId}` : ''}
                  {job?.type === 'old-booking-regiondo-import'
                    ? `, importiert: ${result.imported || 0}, uebersprungen: ${result.skipped || 0}, Fehler: ${
                        Array.isArray(result.failed) ? result.failed.length : 0
                      }`
                    : ''}
                </Typography>
              ) : null}
              {Array.isArray(result?.failed) && result.failed.length > 0 ? (
                <Alert severity='warning'>
                  {result.failed.slice(0, 5).map(item => item.message).join(' | ')}
                </Alert>
              ) : null}
              {Array.isArray(result?.failedEntertainments) && result.failedEntertainments.length > 0 ? (
                <Alert severity='warning'>
                  Fehlgeschlagene Erlebnisse: {result.failedEntertainments.length}. Beim erneuten Start werden
                  nicht erledigte Erlebnisse erneut versucht.
                </Alert>
              ) : null}
            </Stack>
          </Paper>
        ) : null}

      </Stack>
    </Box>
  );
}
